/**
 * BublyUp Device Fingerprint Engine
 */

//
// ─────────────────────────────────────────────────────────────
//   1. BASIC FINGERPRINT ID (UUID) — stored in localStorage
// ─────────────────────────────────────────────────────────────
//

export function getOrCreateFingerprintId() {
  const KEY = 'bublyup_fingerprint_id';
  const CREATED_KEY = 'bublyup_fingerprint_created_at';
  const MAX_AGE_MS = 18 * 30 * 24 * 60 * 60 * 1000;

  let id = localStorage.getItem(KEY);
  const createdAt = Number(localStorage.getItem(CREATED_KEY));

  if (!id || !Number.isFinite(createdAt) || Date.now() - createdAt >= MAX_AGE_MS) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  localStorage.setItem(CREATED_KEY, String(Date.now()));

  return id;
}

//
// ─────────────────────────────────────────────────────────────
//   2. INDEXEDDB STORAGE — survives cookie deletion
// ─────────────────────────────────────────────────────────────
//

export function saveFingerprintToIndexedDB(id) {
  const request = indexedDB.open('bublyup_db', 1);

  request.onupgradeneeded = (event) => {
    const db = event.target.result;
    if (!db.objectStoreNames.contains('fingerprint')) {
      db.createObjectStore('fingerprint', { keyPath: 'key' });
    }
  };

  request.onsuccess = (event) => {
    const db = event.target.result;
    const tx = db.transaction('fingerprint', 'readwrite');
    const store = tx.objectStore('fingerprint');
    store.put({ key: 'device_fingerprint', value: id, expiresAt: Date.now() + 18 * 30 * 24 * 60 * 60 * 1000 });
    tx.oncomplete = () => db.close();
    tx.onerror = () => db.close();
  };
}

export function loadFingerprintFromIndexedDB(callback) {
  const request = indexedDB.open('bublyup_db', 1);

  request.onsuccess = (event) => {
    const db = event.target.result;
    const tx = db.transaction('fingerprint', 'readonly');
    const store = tx.objectStore('fingerprint');
    const getReq = store.get('device_fingerprint');

    getReq.onsuccess = () => {
      const saved = getReq.result;
      if (saved?.expiresAt && saved.expiresAt > Date.now()) {
        callback(saved.value || null);
      } else {
        if (saved) store.delete('device_fingerprint');
        callback(null);
      }
      db.close();
    };
    getReq.onerror = () => db.close();
  };
}

//
// ─────────────────────────────────────────────────────────────
//   3. CANVAS FINGERPRINT — unique per device
// ─────────────────────────────────────────────────────────────
//

export function getCanvasFingerprint() {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  ctx.textBaseline = 'top';
  ctx.font = '16px Arial';
  ctx.fillStyle = '#f60';
  ctx.fillRect(125, 1, 62, 20);
  ctx.fillStyle = '#069';
  ctx.fillText('BublyUp Fingerprint', 2, 15);
  ctx.strokeStyle = '#f00';
  ctx.strokeText('BublyUp Fingerprint', 4, 17);

  const data = canvas.toDataURL();
  let hash = 0;

  for (let i = 0; i < data.length; i++) {
    hash = ((hash << 5) - hash) + data.charCodeAt(i);
    hash |= 0;
  }

  return String(hash);
}

//
// ─────────────────────────────────────────────────────────────
//   4. AUDIO FINGERPRINT — extremely stable across sessions
// ─────────────────────────────────────────────────────────────
//

export async function getAudioFingerprint() {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = ctx.createOscillator();
  const compressor = ctx.createDynamicsCompressor();

  oscillator.type = 'triangle';
  oscillator.frequency.setValueAtTime(10000, ctx.currentTime);

  oscillator.connect(compressor);
  compressor.connect(ctx.destination);

  oscillator.start(0);

  const analyser = ctx.createAnalyser();
  compressor.connect(analyser);

  const data = new Float32Array(analyser.frequencyBinCount);
  analyser.getFloatFrequencyData(data);

  oscillator.stop();

  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    hash = ((hash << 5) - hash) + Math.round(data[i] * 100);
    hash |= 0;
  }

  return String(hash);
}

//
// ─────────────────────────────────────────────────────────────
//   5. FULL FINGERPRINT PAYLOAD — ready to send to backend
// ─────────────────────────────────────────────────────────────
//

export async function generateFullFingerprintPayload() {
  let fingerprintId = getOrCreateFingerprintId();

  try {
    await Promise.race([
      new Promise((resolve) => {
        loadFingerprintFromIndexedDB((idFromDb) => {
          if (idFromDb) fingerprintId = idFromDb;
          resolve();
        });
      }),
      new Promise((resolve) => setTimeout(resolve, 1000))
    ]);
  } catch (error) {
    console.warn("Fingerprint IndexedDB unavailable:", error);
  }

  try {
    saveFingerprintToIndexedDB(fingerprintId);
  } catch (error) {
    console.warn("Fingerprint IndexedDB save failed:", error);
  }

  return {
    fingerprint_id: fingerprintId,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language
  };
}
